/**
 * Locale + direction provider. lang and dir are set TOGETHER at the root
 * (frozen DNA rule); FA is the default working language for Iranian clinics.
 * One mirrored component set — direction comes from CSS logical properties,
 * never from per-locale stylesheets.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { en } from './messages.en';
import { fa } from './messages.fa';
import { setupEn } from './messages.setup.en';
import { setupFa } from './messages.setup.fa';
import { patientsEn } from './messages.patients.en';
import { patientsFa } from './messages.patients.fa';
import { assessEn } from './messages.assess.en';
import { assessFa } from './messages.assess.fa';
import { schedEn } from './messages.sched.en';
import { schedFa } from './messages.sched.fa';
import { sessionsEn } from './messages.sessions.en';
import { sessionsFa } from './messages.sessions.fa';
import { progressEn } from './messages.progress.en';
import { progressFa } from './messages.progress.fa';
import { instrumentsEn } from './messages.instruments.en';
import { instrumentsFa } from './messages.instruments.fa';
import { familyEn } from './messages.family.en';
import { familyFa } from './messages.family.fa';
import { carePlanEn } from './messages.carePlan.en';
import { carePlanFa } from './messages.carePlan.fa';
import { reportsEn } from './messages.reports.en';
import { reportsFa } from './messages.reports.fa';
import { crmEn } from './messages.crm.en';
import { crmFa } from './messages.crm.fa';
import { treatmentEn } from './messages.treatment.en';
import { treatmentFa } from './messages.treatment.fa';

const EN_ALL = {
  ...en,
  ...setupEn,
  ...patientsEn,
  ...assessEn,
  ...schedEn,
  ...sessionsEn,
  ...progressEn,
  ...instrumentsEn,
  ...familyEn,
  ...carePlanEn,
  ...reportsEn,
  ...treatmentEn,
  ...crmEn,
};
const FA_ALL = {
  ...fa,
  ...setupFa,
  ...patientsFa,
  ...assessFa,
  ...schedFa,
  ...sessionsFa,
  ...progressFa,
  ...instrumentsFa,
  ...familyFa,
  ...carePlanFa,
  ...reportsFa,
  ...treatmentFa,
  ...crmFa,
};

export type Locale = 'en' | 'fa';
const STORAGE_KEY = 'zc.locale';

interface LocaleContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (l: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

function readStored(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'en' || v === 'fa' ? v : 'fa';
  } catch {
    return 'fa';
  }
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readStored);
  const dir: 'ltr' | 'rtl' = locale === 'fa' ? 'rtl' : 'ltr';

  useEffect(() => {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo(() => ({ locale, dir, setLocale }), [locale, dir, setLocale]);
  const messages = locale === 'fa' ? FA_ALL : EN_ALL;

  return (
    <LocaleContext.Provider value={value}>
      <IntlProvider locale={locale} messages={messages} defaultLocale="en">
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale outside LocaleProvider');
  return ctx;
}
